import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { SubmitButton } from "../../components/Input";
import { API_URL } from "../../config";
import StateManagedSelect from "react-select";
import { showToast } from "../../components/toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload, faTrash } from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../components/LoadingSpinner";

const TemplateEmailForm = () => {
  const [fieldList, setFieldlist] = useState([]);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [templateName, setTemplateName] = useState("");
  const [subject, setSubject] = useState("");
  const [attachment1, setAttachment1] = useState(null);
  const [attachment2, setAttachment2] = useState(null);
  const [attachmentName1, setAttachmentName1] = useState("");
  const [attachmentName2, setAttachmentName2] = useState("");
  const [oldAttachment1, setOldAttachment1] = useState(null);
  const [oldAttachment2, setOldAttachment2] = useState(null);
  const [showAttachment1, setShowAttachment1] = useState(false);
  const [showAttachment2, setShowAttachment2] = useState(false);
  const [templateId, setTemplateId] = useState("");
  const [senderList, setSenderList] = useState([]);
  const [selectedSender, setSelectedSender] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef1 = useRef(null);
  const fileInputRef2 = useRef(null);
  const token = localStorage.getItem("authToken");
  const { id } = useParams();
  const navigate = useNavigate();
  const [templateMessage, setTemplateMessage] = useState("");

  const fetchSenderList = async () => {
    try {
      const response = await axios.get(
        `${API_URL}settings/emailconfig/dropdown`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const senderOptions =
        response.data?.data?.email?.map((item) => ({
          value: Number(item.value),
          label: `${item.label} (${item.sender})`,
          sender: item.sender,
        })) || [];

      setSenderList(senderOptions);

      if (id) {
        fetchTempEmail(id, senderOptions);
      }
    } catch (error) {
      console.error("Error fetching sender list:", error);
    }
  };

  const fetchFieldList = async () => {
    try {
      const response = await axios.get(
        `${API_URL}settings/template/email/dropdown`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const formattedField = response.data.data.map((field) => ({
        value: field.field_name,
        label: field.field_description,
      }));
      setFieldlist(formattedField);
    } catch (error) {
      console.error("Error fetching field list:", error);
    }
  };

  const fetchTempEmail = async (templateIdParam, senderOptions = []) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}settings/template/email/${templateIdParam}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      const data = response.data.data;

      setTemplateName(data.templatename || "");
      setSubject(data.subject || "");
      setTemplateId(data.template_id || "");
      setAttachmentName1(data.attachment_file_name || "");
      setAttachmentName2(data.attachment2_file_name || "");
      
      // Store old attachment URLs for display and deletion
      if (data.attachment) {
        setOldAttachment1(data.attachment);
        setShowAttachment1(true);
      } else {
        setOldAttachment1(null);
        setShowAttachment1(false);
      }
      if (data.attachment2) {
        setOldAttachment2(data.attachment2);
        setShowAttachment2(true);
      } else {
        setOldAttachment2(null);
        setShowAttachment2(false);
      }

      if (data.sender_type && senderOptions.length > 0) {
        const foundSender = senderOptions.find(
          (opt) => opt.value === Number(data.sender_type)
        );
        setSelectedSender(foundSender || null);
      }

      setTemplateMessage(data.message || "");
    } catch (error) {
      setError(error.message);
      console.error("Error fetching template:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (templateMessage && fieldList.length > 0) {
      const selected = templateMessage
        .split(",")
        .map((fieldValue) =>
          fieldList.find((option) => option.value === fieldValue)
        )
        .filter(Boolean);
      setSelectedOptions(selected);
    }
  }, [templateMessage, fieldList]);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchSenderList(), fetchFieldList()]);
    };
    loadData();
  }, []);

  const addDropdown = () => {
    setSelectedOptions([...selectedOptions, { value: "", label: "" }]);
  };

  const removeDropdown = (index) => {
    setSelectedOptions(selectedOptions.filter((_, i) => i !== index));
  };

  const handleDropdownChange = (selected, index) => {
    const updated = [...selectedOptions];
    updated[index] = selected;
    setSelectedOptions(updated);
  };

  // Handle attachment 1 file change
  const handleAttachment1Change = (e) => {
    if (e.target.files[0]) {
      setAttachment1(e.target.files[0]);
      if (!attachmentName1) {
        setAttachmentName1(e.target.files[0].name);
      }
    }
  };

  // Handle attachment 2 file change
  const handleAttachment2Change = (e) => {
    if (e.target.files[0]) {
      setAttachment2(e.target.files[0]);
      if (!attachmentName2) {
        setAttachmentName2(e.target.files[0].name);
      }
    }
  };

  // Delete attachment 1
  const handleDeleteAttachment1 = () => {
    setAttachment1(null);
    setOldAttachment1(null);
    setAttachmentName1("");
    setShowAttachment1(false);
    if (fileInputRef1.current) {
      fileInputRef1.current.value = "";
    }
  };

  // Delete attachment 2
  const handleDeleteAttachment2 = () => {
    setAttachment2(null);
    setOldAttachment2(null);
    setAttachmentName2("");
    setShowAttachment2(false);
    if (fileInputRef2.current) {
      fileInputRef2.current.value = "";
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    try {
      const formData = new FormData();
      formData.append("template_id", templateId);
      formData.append("templatename", templateName);
      formData.append("subject", subject);
      
      // Handle attachment 1
      if (attachment1) {
        formData.append("attachment", attachment1);
      }
      if (!attachment1 && oldAttachment1) {
        formData.append("old_attachment_url", oldAttachment1);
      }
      formData.append("attachment_file_name", attachmentName1 || "");
      
      // Handle attachment 2
      if (attachment2) {
        formData.append("attachment2", attachment2);
      }
      if (!attachment2 && oldAttachment2) {
        formData.append("old_attachment2_url", oldAttachment2);
      }
      formData.append("attachment2_file_name", attachmentName2 || "");
      
      formData.append(
        "message",
        selectedOptions.map((item) => item.value).join(",")
      );
      formData.append(
        "sender_type",
        selectedSender ? selectedSender.value : ""
      );

      // For debugging - log FormData entries
      for (let pair of formData.entries()) {
        console.log(pair[0] + ': ', pair[1]);
      }

      const url = id
        ? `${API_URL}settings/template/email/save?id=${id}`
        : `${API_URL}settings/template/email/save`;

      const response = await axios.post(url, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.data.success === true) {
        showToast.success(response.data.message);
        navigate("/Settings/TemplateEmail");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showToast.error(error.response?.data?.message || error.message || "Something went wrong");
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        <div className="panel panel-default with-rounded-corner with-shadow">
          <form onSubmit={handleSubmit} encType="multipart/form-data">
            <input type="hidden" name="id" value={id} />
            <div className="panel-heading border-0">
              <span className="panel-title">
                {id ? "Edit" : "Add"} Template Email
              </span>
            </div>
            <div className="panel-body">
              {/* Row 1 */}
              <div className="row align-items-center">
                <div className="col-md-4">
                  <div className="form-group">
                    <label>Template Name</label>
                    <input
                      type="text"
                      name="templatename"
                      className="form-control"
                      placeholder="Enter Template Name"
                      value={templateName}
                      onChange={(e) => setTemplateName(e.target.value)}
                      required
                    />
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="form-group">
                    <label>Subject</label>
                    <input
                      type="text"
                      name="subject"
                      className="form-control"
                      placeholder="Enter Subject"
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Attachment 1 */}
              <div className="row align-items-center">
                <div className="col-md-5">
                  <div className="form-group">
                    <label>Attachment 1</label>
                    <input
                      type="file"
                      name="attachment" 
                      ref={fileInputRef1}
                      className="form-control"
                      accept=".png, .jpg, .jpeg, .pdf, .doc, .docx"
                      onChange={handleAttachment1Change}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="form-group">
                    <label>Attachment 1 File Name</label>
                    <input
                      type="text"
                      name="attachment_file_name"
                      className="form-control"
                      placeholder="Enter Attachment 1 File Name"
                      value={attachmentName1}
                      onChange={(e) => setAttachmentName1(e.target.value)}
                    />
                  </div>
                </div>

                {((showAttachment1 && oldAttachment1) || attachment1) && (
                  <div className="col-md-3">
                    <div className="d-flex gap-2 mt-4">
                      <button
                        className="btn btn-danger mr-1"
                        type="button"
                        onClick={handleDeleteAttachment1}
                        title="Delete Attachment"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                      {oldAttachment1 && (
                        <a
                          className="btn btn-primary"
                          href={oldAttachment1}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Download Attachment"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Attachment 2 */}
              <div className="row align-items-center">
                <div className="col-md-5">
                  <div className="form-group">
                    <label>Attachment 2</label>
                    <input
                      type="file"
                      name="attachment2" 
                      ref={fileInputRef2}
                      className="form-control"
                      accept=".png, .jpg, .jpeg, .pdf, .doc, .docx"
                      onChange={handleAttachment2Change}
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="form-group">
                    <label>Attachment 2 File Name</label>
                    <input
                      type="text"
                      name="attachment2_file_name"
                      className="form-control"
                      placeholder="Enter Attachment 2 File Name"
                      value={attachmentName2}
                      onChange={(e) => setAttachmentName2(e.target.value)}
                    />
                  </div>
                </div>

                {((showAttachment2 && oldAttachment2) || attachment2) && (
                  <div className="col-md-3">
                    <div className="d-flex gap-2 mt-4">
                      <button
                        className="btn btn-danger mr-1"
                        type="button"
                        onClick={handleDeleteAttachment2}
                        title="Delete Attachment"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                      {oldAttachment2 && (
                        <a
                          className="btn btn-primary"
                          href={oldAttachment2}
                          download
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Download Attachment"
                        >
                          <FontAwesomeIcon icon={faDownload} />
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Sender Type */}
              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Sender Type</label>
                    <StateManagedSelect
                      options={senderList}
                      value={selectedSender}
                      onChange={setSelectedSender}
                      placeholder="Select Sender Type"
                      isClearable
                    />
                  </div>
                </div>
              </div>

              {/* Field Dropdowns */}
              <div className="row clonelist">
                <div className="col-md-4">
                  <table className="table m-b-0">
                    <thead>
                      <tr>
                        <th style={{ textAlign: "center", width: "30%" }}>
                          Name
                        </th>
                        <th style={{ textAlign: "center", width: "10%" }}>
                          Delete
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOptions.map((selected, index) => (
                        <tr key={index}>
                          <td>
                            <StateManagedSelect
                              options={fieldList}
                              value={selected}
                              onChange={(selected) =>
                                handleDropdownChange(selected, index)
                              }
                              placeholder="Select..."
                              isClearable
                            />
                          </td>
                          <td
                            style={{
                              textAlign: "center",
                              verticalAlign: "middle",
                            }}
                          >
                            <button
                              type="button"
                              className="btn btn-danger btn-gradient delete_row btn-alt pt5 pb5 mn"
                              title="Delete Record"
                              onClick={() => removeDropdown(index)}
                            >
                              X Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan="2">
                          <button
                            type="button"
                            className="addPick btn btn-success"
                            onClick={addDropdown}
                          >
                            Add Data
                          </button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="col-md-12">
                <SubmitButton
                  pageBack={"/Settings/TemplateEmail"}
                  handleSubmit={handleSubmit}
                />
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TemplateEmailForm;